import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { AdministrationManagementService } from '@tsin-core/services/http/administration-management.service';
import { tap, catchError, throwError } from 'rxjs';
import {
  CreateAdministration,
  DeleteAdministration,
  GetAdministrations,
  UpdateAdministration,
} from './administration-management.action';
import { IAdministration } from '@tsin-core/models/administration-management';

export interface AdministrationManagementStateModel {
  administration: IAdministration[];
  loading: LoadingState;
}

@State<AdministrationManagementStateModel>({
  name: 'administrationManagementState',
  defaults: {
    administration: [],
    loading: LoadingState.notLoading,
  },
})
@Injectable()
export class AdmninistrationManagementState {
  administrationManagementService: AdministrationManagementService = inject(
    AdministrationManagementService
  );

  @Selector()
  static listAdministration(
    state: AdministrationManagementStateModel
  ): IAdministration[] {
    return state?.administration ?? [];
  }

  @Selector()
  static getLoading(state: AdministrationManagementStateModel): LoadingState {
    return state.loading;
  }

  @Action(CreateAdministration)
  createAdministration(
    ctx: StateContext<AdministrationManagementStateModel>,
    action: CreateAdministration
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.administrationManagementService
      .createAdministration(action.payload)
      .pipe(
        tap((newAdministration: IAdministration) => {
          const state = ctx.getState();
          ctx.patchState({
            administration: [...state.administration, newAdministration],
            loading: LoadingState.notLoading,
          });
        }),
        catchError((error) => {
          ctx.patchState({ loading: LoadingState.notLoading });
          throw error;
        })
      );
  }

  @Action(GetAdministrations)
  getAdministrations(
    ctx: StateContext<AdministrationManagementStateModel>,
    action: GetAdministrations
  ) {
    ctx.patchState({ loading: LoadingState.loadingList });

    return this.administrationManagementService.getAdministrations().pipe(
      tap((administration: IAdministration[]) => {
        ctx.patchState({ administration, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        console.error('Error fetching administrations:', error);
        ctx.patchState({ loading: LoadingState.notLoading });
        return throwError(() => error);
      })
    );
  }

  @Action(UpdateAdministration)
  updateAdministration(
    ctx: StateContext<AdministrationManagementStateModel>,
    action: UpdateAdministration
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.administrationManagementService
      .updateAdministration(action.updatePayload)
      .pipe(
        tap((updatedAdministration: IAdministration) => {
          const state = ctx.getState();

          const updatedAdministrations = state.administration.map(
            (administration) =>
              administration.id === updatedAdministration.id
                ? updatedAdministration
                : administration
          );

          ctx.patchState({
            administration: updatedAdministrations,
            loading: LoadingState.notLoading,
          });
        }),
        catchError((error) => {
          ctx.patchState({ loading: LoadingState.notLoading });
          throw error;
        })
      );
  }

  @Action(DeleteAdministration)
  deleteAdministration(
    ctx: StateContext<AdministrationManagementStateModel>,
    action: DeleteAdministration
  ) {
    ctx.patchState({ loading: LoadingState.loadingDelete });
    return this.administrationManagementService
      .deleteAdministration(action.id)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const updatedAdministrations = state.administration.filter(
            (administration) => administration.id !== action.id
          );
          ctx.patchState({
            administration: updatedAdministrations,
            loading: LoadingState.notLoading,
          });
        }),
        catchError((error) => {
          ctx.patchState({ loading: LoadingState.notLoading });
          return throwError(() => error);
        })
      );
  }
}
